import axios from 'axios';
import { subirArchivo as subirAStorage } from '../paginaWeb/paginaWeb.api';

// "Datos de la obra": la ficha única de la obra (lisual-admin-backend, /api/datos-obra). La leen y escriben
// Obra › Datos de la obra y Marketing › Página web, así el cliente no carga dos veces lo mismo.
// En local se apunta a un backend propio con REACT_APP_DATOS_OBRA_API_URL (datos y archivos en una carpeta de la PC).
const BASE = (process.env.REACT_APP_DATOS_OBRA_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8081').replace(/\/$/, '');
const api = axios.create({ baseURL: `${BASE}/api/datos-obra`, timeout: 20000 });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authLisualToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
// Sólo local: los archivos van a la carpeta del servidor de prueba en vez de Firebase Storage.
const SUBIDA_LOCAL = process.env.NODE_ENV === 'development' && process.env.REACT_APP_DATOS_OBRA_UPLOAD_LOCAL === '1';

// La ubicación exacta NO va acá: es la de la obra (webback, PUT /api/camaras/:feed/obra/ubicacion) y la guarda
// el selector obras/components/UbicacionObra.jsx. El GET de la ficha la devuelve para mostrarla y contarla.

export const errorDatos = (e) => e?.response?.data?.message || e?.message || 'Algo salió mal';

const MB = 1024 * 1024;
// Lo mismo que valida el backend (para avisar antes de subir).
export const CATEGORIAS = {
  planos: { nombre: 'Planos', desc: 'Plantas, cortes, implantación. PDF, DWG o imagen.', accept: '.pdf,.dwg,.dxf,image/png,image/jpeg,image/webp', ext: ['pdf', 'dwg', 'dxf', 'png', 'jpg', 'jpeg', 'webp'], max: 100 * MB, link: true },
  renders: { nombre: 'Renders', desc: 'Imágenes del proyecto terminado.', accept: 'image/png,image/jpeg,image/webp,image/avif', ext: ['png', 'jpg', 'jpeg', 'webp', 'avif'], max: 25 * MB },
  brochure: { nombre: 'Brochure', desc: 'El PDF comercial del proyecto.', accept: 'application/pdf', ext: ['pdf'], max: 50 * MB, link: true },
  modelo3d: { nombre: 'Modelo 3D', desc: 'SketchUp, OBJ, GLB, IFC (o .zip). También podés pegar un link.', accept: '.skp,.obj,.glb,.gltf,.ifc,.fbx,.dae,.3ds,.zip,.rar', ext: ['skp', 'obj', 'glb', 'gltf', 'ifc', 'fbx', 'dae', '3ds', 'zip', 'rar'], max: 300 * MB, link: true },
  logo: { nombre: 'Logo de la empresa', desc: 'PNG o SVG, mejor con fondo transparente.', accept: 'image/png,image/jpeg,image/webp,image/svg+xml,.svg,.pdf,.ai,.eps', ext: ['png', 'jpg', 'jpeg', 'webp', 'svg', 'pdf', 'ai', 'eps'], max: 10 * MB },
  cronograma: { nombre: 'Cronograma de obra (Gantt)', desc: 'Tu diagrama de Gantt o el archivo de MS Project. Opcional.', accept: '.mpp,.xml,.pdf,.xlsx,.xls,.csv,image/png,image/jpeg', ext: ['mpp', 'xml', 'pdf', 'xlsx', 'xls', 'csv', 'png', 'jpg', 'jpeg'], max: 25 * MB },
};
/** Categorías de un solo archivo: el nuevo reemplaza al anterior. */
export const UN_SOLO_ARCHIVO = ['logo', 'cronograma'];
export const JORNADA_DEFAULT = { dias: [1, 2, 3, 4, 5], desde: '08:00', hasta: '17:00', sabado: null };
// Subir el cronograma incluye leerlo (puede tardar si es un PDF): más tiempo que el resto.
const LECTURA_MS = 120000;
export const MAX_POR_CATEGORIA = 30;

export const extension = (n) => (String(n).toLowerCase().match(/\.([a-z0-9]{1,5})$/) || [])[1] || '';
export const tamano = (b) => (b >= MB ? (b / MB).toFixed(1).replace('.', ',') + ' MB' : Math.max(1, Math.round(b / 1024)) + ' KB');
export const esImagen = (a) => /^(png|jpe?g|webp|avif|svg|gif)$/.test(extension(a?.nombre || '')) || /^image\//.test(a?.tipo || '');

/** Revisa tipo y tamaño antes de subir. Devuelve el mensaje de error o '' si está bien. */
export function problemaArchivo(cat, f) {
  const c = CATEGORIAS[cat];
  if (!c) return 'Categoría desconocida';
  if (!c.ext.includes(extension(f.name))) return `"${f.name}": acá aceptamos ${c.ext.join(', ')}.`;
  if (f.size > c.max) return `"${f.name}" pesa ${tamano(f.size)}; el máximo es ${Math.round(c.max / MB)} MB. Si es más grande, pegá un link (Drive, WeTransfer).`;
  return '';
}

// caché corta por feed: Datos de la obra y Página web piden lo mismo
const cache = new Map();
export function getDatosObra(feedId, { fresco = false } = {}) {
  if (!feedId) return Promise.resolve(null);
  if (fresco || !cache.has(feedId)) {
    const p = api.get(`/${encodeURIComponent(feedId)}`).then((r) => r.data).catch((e) => { cache.delete(feedId); throw e; });
    cache.set(feedId, p);
  }
  return cache.get(feedId);
}
const guardarCache = (feedId) => (r) => { cache.set(feedId, Promise.resolve(r.data)); return r.data; };

export const guardarDatos = (feedId, cambios) => api.put(`/${encodeURIComponent(feedId)}`, cambios).then(guardarCache(feedId));
export const quitarArchivo = (feedId, id) => api.delete(`/${encodeURIComponent(feedId)}/archivos/${encodeURIComponent(id)}`).then(guardarCache(feedId));
export const agregarLink = (feedId, categoria, url, nombre) => api.post(`/${encodeURIComponent(feedId)}/archivos`, { categoria, link: true, url, nombre }).then(guardarCache(feedId));
export const reintentarCronograma = (feedId) => api.post(`/${encodeURIComponent(feedId)}/cronograma/analizar`, null, { timeout: LECTURA_MS }).then(guardarCache(feedId));
export const confirmarDatos = (feedId) => api.post(`/${encodeURIComponent(feedId)}/confirmar`).then(guardarCache(feedId));

/**
 * Sube un archivo al mismo Storage que usa Página web (solicitudes-web/<feed>/datos-obra/<categoria>/) y lo
 * registra en la ficha. `origen`: 'datos-obra' o 'pagina-web'. Devuelve la ficha actualizada.
 */
export async function subirArchivo(feedId, categoria, file, { onProgress, origen = 'datos-obra' } = {}) {
  let subido;
  if (SUBIDA_LOCAL) {
    const r = await axios.post(`${BASE}/api/datos-obra-dev/subir/${encodeURIComponent(feedId)}`, file, {
      params: { categoria, nombre: file.name },
      headers: { 'Content-Type': 'application/octet-stream' },
      onUploadProgress: (e) => onProgress && onProgress(e.loaded),
    });
    subido = r.data;
  } else {
    subido = await subirAStorage(file, `solicitudes-web/${feedId}/datos-obra/${categoria}`, onProgress);
  }
  const r = await api.post(`/${encodeURIComponent(feedId)}/archivos`, { categoria, nombre: file.name, bytes: file.size, tipo: file.type, url: subido.url, origen }, categoria === 'cronograma' ? { timeout: LECTURA_MS } : undefined);
  return guardarCache(feedId)(r);
}
